import { format, fromUnixTime } from 'date-fns';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '../../../../../../components/Box';
import Icon from '../../../../../../components/Icon';
import IconWrap from '../../../../../../components/IconWrap';
import Place from '../../../../../../components/Place';
import Rating from '../../../../../../components/Rating';
import StatusCircle from '../../../../../../components/StatusCircle';
import StatusCopy from '../../../../../../components/StatusCopy';
import TableAdvanced from '../../../../../../components/TableAdvanced';
import Typography from '../../../../../../components/Typography';
import useTheme from '../../../../../../hooks/useTheme';
import i18n from '../../../../../../i18n';
import { getLabelDateStartJob } from '../../../../../../utils/helpers';
import { makeStyles } from '../../../../../../utils/styles';

const useStyle = makeStyles(css => ({
    subTypography: css`
        ${({ theme }) => theme.fonts.mulishRegular};
        font-size: 11px;
        color: ${({ theme }) => theme.colors.dimGrey};
    `,
    boxStatus: css`
        display: flex;
        align-items: center;
    `,
    boxCreated: css`
        margin-left: 35px;
        white-space: nowrap;
    `,
    boxPlace: css`
        margin-left: 35px;
    `,
    boxRating: css`
        line-height: 1.8em;
    `
}));

const TableJobs = ({ jobs, renderRowSubComponent }) => {
    const { t } = useTranslation()
    const styles = useStyle();
    const theme = useTheme();
    const columns = useMemo(
        () => [
            {
                Header: t('Status'),
                accessor: 'status',
                disableResizing: true,
                Cell: ({ row, cell: { value } }) => {
                    const place = row.original?.place_id;
                    return (
                        <Box customStyle={styles.boxStatus}>
                            <Box customStyle={styles.boxPlace}>
                                <Place
                                    logo={place?.logo}
                                />
                            </Box>
                            <StatusCircle variant="absolute" value={value} />
                        </Box>
                    )
                },
                subAccessor: 'created_at',
                SubCell: (cellProps) => {
                    const date = new Date(cellProps.value);
                    return (
                        <Box customStyle={styles.boxCreated}>
                            <Typography as="h3">
                                {t("Zlecenie opublikowano")}
                            </Typography>
                            <Typography>
                                {format(date, 'dd.MM.yyyy, HH:mm')}
                            </Typography>
                        </Box>
                    )
                },
                width: 70
            },
            {
                Header: t('Data świadczenia pracy'),
                accessor: 'time_start',
                Cell: ({ cell: { value } }) => {
                    const date = fromUnixTime(value);
                    return (
                        <Box>
                            <Typography>
                                {format(date, 'dd.MM.yyyy')}h
                            </Typography>
                            <Typography customStyle={styles.subTypography}>
                                {getLabelDateStartJob(date)}
                            </Typography>
                        </Box>
                    )
                }
            },
            {
                Header: t('Nazwa zlecenia'),
                accessor: 'title',
                subAccessor: 'time_end',
                SubCell: (cellProps) => {
                    const timeEnd = fromUnixTime(cellProps?.value);
                    return (
                        <Box>
                            <Typography as="h3">
                                {t("Data trwania zlecenia")}
                            </Typography>
                            <Typography>
                                {format(timeEnd, 'dd.MM.yyyy')}
                            </Typography>
                        </Box>
                    )
                }
            },
            {
                Header: t('Stanowisko'),
                accessor: 'profession',
                subAccessor: 'start_ranking',
                SubCell: (cellProps) => {
                    const rate = cellProps?.value;
                    return (
                        <Box>
                            <Typography as="h3">
                                {t("Kandydaci od")}
                            </Typography>
                            <Box customStyle={styles.boxRating}>
                                <Rating rate={rate} />
                            </Box>
                        </Box>
                    )
                }
            },
            {
                Header: t('Godziny'),
                accessor: 'time_hours',
                Cell: ({ row, cell: { value } }) => {
                    return (
                        <Box>
                            <Typography>
                                {row.original?.time_range}
                            </Typography>
                            <Typography customStyle={styles.subTypography}>
                                {value}
                            </Typography>
                        </Box>
                    )
                }
            },
            {
                Header: t('Liczba miejsc'),
                accessor: 'vacancies',
                Cell: ({ cell: { value } }) => {
                    return (
                        <Typography font={theme.fonts.mulishBold}>
                            {value}
                        </Typography>
                    )
                }
            },
            {
                Header: t('Liczba zgłoszeń'),
                accessor: 'vacancies_diff',
                Cell: ({ row, cell: { value } }) => {
                    const status = row.original?.status;
                    return (
                        <StatusCopy value={status}>
                            <Typography font={theme.fonts.mulishBold}>
                                {value}
                            </Typography>
                        </StatusCopy>
                    )
                }
            },
            {
                Header: () => null,
                id: 'expander',
                disableResizing: true,
                Cell: ({ row }) => (
                    <IconWrap rotate={row.isExpanded ? 0 : -180}>
                        <Icon name="arrow" />
                    </IconWrap>
                ),
                width: 30
            },
        ],
        // eslint-disable-next-line
        [i18n.language]
    );

    const data = useMemo(() => jobs, [jobs]);

    return (
        <TableAdvanced
            columns={columns}
            data={data}
            offsetLeft={12}
            renderRowSubComponent={renderRowSubComponent}
            pageSize={100}
            sortBy={[{
                id: 'time_start',
                desc: true
            }]}
        />
    )
}

export default TableJobs;
